import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { Notification } from '@/lib/notifications/notification-types';
import { debounce } from 'lodash';
import { NotificationsCodeMap } from '@/lib/types';
import useUserStore from '@/lib/hooks/zustand-stores/use-user-store';
import useWebSocketStore from '@/lib/hooks/zustand-stores/use-websocket-store';
import { useAuth } from '@clerk/clerk-react'
import { setClickedActionInNotification } from '@/app/actions/interactions';
import { getAllNotifications,
  getUnreadNotifications,
  markNotificationsAsReadByIds } from '@/app/actions/notifications';
import { logger } from '@/lib/logger';

// Set to track notifications we've already processed to prevent infinite loops
// Using notificationId instead of chatId gives more precise control
const processedNotifications = new Set<string>();

export type FilterType = 'all' | 'messages' | 'comments' | 'upvotes' | 'views' | 'downvotes' | 'follows' | 'mentions' | 'chat activities' | 'interactions';

const POLLING_INTERVAL = process.env.NEXT_PUBLIC_NOTIFICATION_POLLING_INTERVAL
  ? parseInt(process.env.NEXT_PUBLIC_NOTIFICATION_POLLING_INTERVAL)
  : 1000;

type PollingInterval = ReturnType<typeof setInterval>;

interface NotificationState {
  unreadNotifications: Notification[];
  allNotifications: Notification[];
  unreadNotificationsByType: { [key: string]: Notification[] };
  loading: boolean;
  allNotificationsLoading: boolean;
  limit: number;
  page: number;
  currentFilter: FilterType;
  pollingInterval: PollingInterval | null;
}

interface NotificationActions {
  fetchAllNotifications: (currentFilter?: FilterType, page?: number) => Promise<void>;
  fetchUnreadNotifications: () => Promise<void>;
  markAllAsRead: () => Promise<void>;
  markAsRead: (notificationIds: string[]) => Promise<void>;
  refreshNotifications: () => Promise<void>;
  setLimit: (limit: number) => void;
  setPage: (page?: number) => void;
  setCurrentFilter: (filter: FilterType) => void;
  setNotificationActionsClicked: (notificationId: string, actions: string[]) => Promise<void>;
  initializeNotifications: () => void;
  initializeUnreadNotifications: () => void;
  initializeAllNotifications: () => void;
  stopPolling: () => void;
  getUnreadCountsForChats: () => { [chatId: string]: number };
  handleNewNotification: (notification: Notification) => void;
  getNotificationsByChatId: (chatId: string) => Notification[];
  updateNotificationMessage: (notificationId: string, newMessage: string) => void;
  refreshFirstPage: () => Promise<void>;
}

type Store = NotificationState & NotificationActions;

const initialState: NotificationState = {
  unreadNotifications: [],
  allNotifications: [],
  unreadNotificationsByType: {},
  loading: false,
  allNotificationsLoading: true,
  limit: 20,
  page: 1,
  currentFilter: 'all',
  pollingInterval: null,
};

const useNotificationStore = create<Store>()(
  devtools(
    persist(
      immer((set, get) => ({
        ...initialState,

        fetchAllNotifications: async (currentFilter = 'all', page?: number) => {
          const user = useUserStore.getState().user;
          if (!user?._id || !get().page) return;
          set({ loading: true });
          try {
            const pageToUse = page ? page : get().page;
            const fetchedNotifications = await getAllNotifications(user._id, get().limit, pageToUse);
            set(state => {
              // Clear existing notifications if this is page 1
              if (pageToUse === 1) {
                state.allNotifications = fetchedNotifications;
              } else {
                const combined = [...state.allNotifications, ...fetchedNotifications];
                state.allNotifications = Array.from(new Set(combined.map(n => n._id)))
                  .map(id => combined.find(n => n._id === id))
                  .filter(Boolean);
              }
              // Always sort by date
              state.allNotifications.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
            });
          } catch (error) {
            console.error('Failed to fetch all notifications:', error);
          } finally {
            set({ loading: false });
          }
        },

        fetchUnreadNotifications: async () => {
          const user = useUserStore.getState().user;
          if (!user?._id) return;
          try {
            const fetchedUnreadNotifications = await getUnreadNotifications(user._id);
            set(state => {
              // Replace all unread notifications
              state.unreadNotifications = fetchedUnreadNotifications;
              // Regroup by type
              state.unreadNotificationsByType = groupNotificationsByType(fetchedUnreadNotifications);
              // Update read status in allNotifications
              state.allNotifications = state.allNotifications.map(n => {
                const unreadVersion = fetchedUnreadNotifications.find(un => un._id === n._id);
                return unreadVersion || { ...n, isRead: true };
              });
            });
          } catch (error) {
            console.error('Failed to fetch unread notifications:', error);
          }
        },

        markAllAsRead: async () => {
          try {
            // if (!isConnectionReady()) {
            //   console.warn('WebSocket not ready, skipping mark all as read');
            //   return;
            // }
            const allIds = get().unreadNotifications.map(n => n._id);
            // Make sure to mark all notifications as read across all organizations
            await markNotificationsAsReadByIds(allIds, true);
            set(state => {
              state.unreadNotifications = [];
              state.allNotifications = state.allNotifications.map(n => ({ ...n, isRead: true }));
              state.unreadNotificationsByType = {};
            });
          } catch (error) {
            console.error('Error marking all notifications as read:', error);
          }
        },

        markAsRead: async (notificationIds: string[]) => {
          try {
            await markNotificationsAsReadByIds(notificationIds, true);
            set(state => {
              state.unreadNotifications = state.unreadNotifications.filter(n => !notificationIds.includes(n._id));
              state.allNotifications = state.allNotifications.map(n =>
                notificationIds.includes(n._id) ? { ...n, isRead: true } : n
              );
              Object.keys(state.unreadNotificationsByType).forEach(type => {
                state.unreadNotificationsByType[type] = state.unreadNotificationsByType[type].filter(n => !notificationIds.includes(n._id));
                if (state.unreadNotificationsByType[type].length === 0) {
                  delete state.unreadNotificationsByType[type];
                }
              });
            });
          } catch (error) {
            console.error('Error marking notification as read:', error);
          }
        },

        refreshNotifications: async () => {
          await get().fetchAllNotifications();
          if (!isConnectionReady()) {
            await get().fetchUnreadNotifications();
          }
        },

        refreshFirstPage: async () => {
          await get().fetchAllNotifications(undefined, 1);
          if (!isConnectionReady()) {
            await get().fetchUnreadNotifications();
          }
        },

        setLimit: (limit: number) => set({ limit }),

        setPage: (page?: number) => set(state => {
          if (page === undefined) {
            // If no page is provided, increment the current page
            state.page = state.page === null ? 1 : state.page + 1;
          } else {
            // If a page is provided, set it directly
            state.page = page;
          }
        }),
        setCurrentFilter: (filter: FilterType) => set({ currentFilter: filter }),

        setNotificationActionsClicked: async (notificationId: string, actions: string[]) => {
          try {
            set(state => {
              state.unreadNotifications = state.unreadNotifications.map(notification => {
                if (notification._id === notificationId) {
                  return {
                    ...notification,
                    actions: notification.actions?.map(action =>
                      actions.includes(action.action) ? { ...action, clicked: true } : action
                    )
                  };
                }
                return notification;
              });

              state.allNotifications = state.allNotifications.map(notification => {
                if (notification._id === notificationId) {
                  return {
                    ...notification,
                    actions: notification.actions?.map(action =>
                      actions.includes(action.action) ? { ...action, clicked: true } : action
                    )
                  };
                }
                return notification;
              });

              Object.keys(state.unreadNotificationsByType).forEach(type => {
                state.unreadNotificationsByType[type] = state.unreadNotificationsByType[type].map(notification => {
                  if (notification._id === notificationId) {
                    return {
                      ...notification,
                      actions: notification.actions?.map(action =>
                        actions.includes(action.action) ? { ...action, active: false } : action
                      )
                    };
                  }
                  return notification;
                });
              });
            });
            await setClickedActionInNotification(notificationId, actions);
          } catch (error) {
            console.error('Failed to set notification actions as inactive:', error);
          }
        },

        initializeNotifications: () => {
          const user = useUserStore.getState().user;
          if (!user?._id) return;

          // Reset state to initial
          set(state => {
            state.unreadNotifications = [];
            state.allNotifications = [];
            state.unreadNotificationsByType = {};
            state.page = 1;
          });

          // Fetch fresh data
          get().fetchUnreadNotifications();
          get().fetchAllNotifications();
        },

        initializeAllNotifications: () => {
          const user = useUserStore.getState().user;
          if (!user?._id) return;

          // Reset state to initial
          set(state => {
            state.unreadNotifications = [];
            state.allNotifications = [];
            state.unreadNotificationsByType = {};
            state.page = 1;
          });

          // Fetch fresh data
          get().fetchUnreadNotifications();
          get().fetchAllNotifications();
        },

        initializeUnreadNotifications: () => {
          const user = useUserStore.getState().user;
          if (!user?._id) return;

          // Only fetch unread notifications once
          get().fetchUnreadNotifications();
        },

        stopPolling: () => {
          const { pollingInterval } = get();
          if (pollingInterval) {
            clearInterval(pollingInterval);
            set({ pollingInterval: null });
          }
        },

        getUnreadCountsForChats: () => {
          const { unreadNotificationsByType } = get();
          const unreadCounts: { [chatId: string]: number } = {};
          if (unreadNotificationsByType.chat) {
            unreadNotificationsByType.chat.forEach(notification => {
              const chatItem = notification.item.find(item => item.code === NotificationsCodeMap.chat);
              if (chatItem) {
                const chatId = chatItem.id.toString();
                unreadCounts[chatId] = (unreadCounts[chatId] || 0) + 1;
              }
            });
          }
          return unreadCounts;
        },

        handleNewNotification: (notification: Notification) => {
          logger.info('Processing new notification:', {
            notificationId: notification._id,
            type: notification.type,
            userId: notification.userId
          });
          
          // Skip if we've already processed this notification
          if (processedNotifications.has(notification._id)) {
            logger.debug(`Skipping already processed notification: ${notification._id}`);
            return;
          }
          
          // Add to processed set to prevent reprocessing
          processedNotifications.add(notification._id);
          
          // Limit the size of the set to prevent memory leaks (keep latest 1000)
          if (processedNotifications.size > 1000) {
            // Remove the oldest items (first 200)
            const toRemove = Array.from(processedNotifications).slice(0, 200);
            toRemove.forEach(id => processedNotifications.delete(id));
          }
           
          // Update notifications state
          set(state => {
            // Add to unread notifications if not already present
            if (!state.unreadNotifications.some(n => n._id === notification._id)) {
              state.unreadNotifications = [notification, ...state.unreadNotifications];
              state.unreadNotificationsByType = groupNotificationsByType(state.unreadNotifications);
              logger.info('Added new unread notification', {
                notificationId: notification._id,
                totalUnread: state.unreadNotifications.length
              });
            }

            // Add to all notifications if not already present and it's the first page
            if (state.page === 1 && !state.allNotifications.some(n => n._id === notification._id)) {
              state.allNotifications = [notification, ...state.allNotifications];
              logger.info('Added notification to all notifications list', {
                notificationId: notification._id,
                totalAll: state.allNotifications.length
              });
            }
          });
           
          // Automatically update chat list for chat-related notifications
          if (
            notification.type === 'NEW_MESSAGE_NOTIFICATION' ||
            notification.type === 'ADDED_TO_CHAT_NOTIFICATION' ||
            notification.type === 'CHAT_UPDATE_NOTIFICATION' ||
            notification.type === 'CHAT_INVITATION_NOTIFICATION'
          ) {
            const chatListStore = require('@/lib/hooks/zustand-stores/use-chat-list-store').default;
            
            // For ADDED_TO_CHAT_NOTIFICATION notifications
            if (notification.type === 'ADDED_TO_CHAT_NOTIFICATION' && notification.item) {
              const chatItem = notification.item.find(item => item.code === 1);
              if (chatItem && chatItem.id) {
                const chatId = chatItem.id.toString();
                logger.info(`Notification triggered chat refresh for new chat: ${chatId}`);
                chatListStore.getState().refreshChatInList(chatId);
              }
            }
            
            // For other chat notifications that have a chatId
            else if (notification.chatId) {
              const chatId = notification.chatId.toString();
              logger.info(`Notification triggered chat refresh for chatId: ${chatId}`);
              chatListStore.getState().refreshChatInList(chatId);
            }
          }
        },

        getNotificationsByChatId: (chatId: string) => {
          const { unreadNotifications } = get();
          return unreadNotifications.filter(notification => {
            const isCorrectChatId = notification.item.some(item =>
              item.code === NotificationsCodeMap.chat && item.id.toString() === chatId
            );
            return isCorrectChatId;
          });
        },

        updateNotificationMessage: (notificationId: string, newMessage: string) => {
          set(state => {
            // Update the message in unreadNotifications
            state.unreadNotifications = state.unreadNotifications.map(notification =>
              notification._id === notificationId ? { ...notification, message: newMessage } : notification
            );

            // Update the message in allNotifications
            state.allNotifications = state.allNotifications.map(notification =>
              notification._id === notificationId ? { ...notification, message: newMessage } : notification
            );

            // Update the message in unreadNotificationsByType
            Object.keys(state.unreadNotificationsByType).forEach(type => {
              state.unreadNotificationsByType[type] = state.unreadNotificationsByType[type].map(notification =>
                notification._id === notificationId ? { ...notification, message: newMessage } : notification
              );
            });
          });
        },
      })),
      {
        name: 'notification-storage',
        version: 1, // Add version to handle schema changes
        onRehydrateStorage: () => (state) => {
          // When store is rehydrated from storage, fetch fresh data
          if (state && useUserStore.getState().user?._id) {
            state.initializeNotifications();
          }
        },
      }
    )
  )
);

function groupNotificationsByType(notifications: Notification[]): { [key: string]: Notification[] } {
  const groupedNotifications: { [key: string]: Set<Notification> } = {};

  notifications.forEach(notification => {
    if (Array.isArray(notification.item)) {
      notification.item.forEach((item: { name: string }) => {
        const type = item.name.toLowerCase();
        if (!groupedNotifications[type]) {
          groupedNotifications[type] = new Set();
        }
        groupedNotifications[type].add(notification);
      });
    }
  });

  // Convert Sets back to arrays
  return Object.fromEntries(
    Object.entries(groupedNotifications).map(([type, notificationSet]) => [
      type,
      Array.from(notificationSet)
    ])
  );
}

const isConnectionReady = () => {
  const { connectionStatus } = useWebSocketStore.getState();
  return connectionStatus === 'connected';
}

export default useNotificationStore;
